import { createSelector } from '@ngrx/store';
import { createSlice, noopReducer, PayloadAction } from 'ngrx-slice';
import { createSliceEntityAdapter, EntityState } from 'ngrx-slice/entity';
import {
  ApiErrorResponse,
  ApiErrorResponseWithId,
} from 'src/app/shared/interfaces/api-error-response';

import { ProductDetailedResponse, ProductPageResponse } from '@app/api/models';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { PageRequest } from '@shared/interfaces/page-request.interface';
import { PageResponse } from '@shared/interfaces/page-response.interface';

import { ContentStatus, Packaging, Unit } from '../interfaces/models';

export interface ProductPageRequest extends PageRequest {
  status?: ContentStatus;
  brand?: string;
  name?: string;
  ean?: string;
  unit?: Unit;
  netAmount?: number;
  packaging?: Packaging;
}

export interface State {
  // products - get list
  productsFilter: ProductPageRequest;
  products: EntityState<ProductPageResponse>;
  productsLoading: boolean;
  productsLoaded: boolean;
  productsLoadError: boolean;
  totalProducts: number;
  // products - get one product to edit
  editedProduct: ProductDetailedResponse | undefined;
  editedProductLoading: boolean;
  editedProductLoadError: boolean;
  // products - save
  productSaving: boolean;
  productSaveError?: ApiErrorResponse;
  // products - delete
  productDeleting: boolean;
  productDeleteError: boolean;
}

export const productAdapter = createSliceEntityAdapter<ProductPageResponse>({
  selectId: (a) => a.packagingUnitId ?? '',
});

export const initialState: State = {
  // products - get list
  productsFilter: {
    status: ContentStatus.Draft,
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
  },
  products: productAdapter.getInitialState(),
  productsLoading: false,
  productsLoaded: false,
  productsLoadError: false,
  totalProducts: 0,
  // products - get one product to edit
  editedProduct: undefined,
  editedProductLoading: false,
  editedProductLoadError: false,
  // products - save
  productSaving: false,
  productSaveError: undefined,
  // products - delete
  productDeleting: false,
  productDeleteError: false,
};

export const {
  actions: ProductActions,
  selectors,
  ...ProductsFeature
} = createSlice({
  name: 'products',
  initialState,
  reducers: {
    loadProducts: {
      trigger: (state, filter: PayloadAction<ProductPageRequest>) => {
        productAdapter.setAll(state.products, []);
        state.productsFilter = filter;
        state.productsLoading = true;
      },
      success: (
        state,
        action: PayloadAction<PageResponse<ProductPageResponse>>
      ) => {
        productAdapter.setAll(state.products, action.content);
        state.totalProducts = action?.totalElements ?? 0;
        state.productsLoading = false;
        state.productsLoaded = true;
        state.productsLoadError = false;
      },
      failure: (state) => ({
        ...state,
        productsLoading: false,
        productsLoadError: true,
      }),
    },
    loadEditedProduct: {
      trigger: (
        state,
        action: PayloadAction<{ packagingUnitId: string; editable: boolean }>
      ): State => ({
        ...state,
        ...action,
        editedProductLoading: true,
      }),
      success: (state, action: PayloadAction<ProductDetailedResponse>) => {
        state.editedProduct = action;
        state.editedProductLoading = false;
        state.editedProductLoadError = false;
      },
      failure: (state) => ({
        ...state,
        editedProductLoadError: true,
        editedProductLoading: false,
      }),
    },
    clearEditedProduct: {
      trigger: (state): State => ({
        ...state,
        editedProduct: undefined,
        editedProductLoading: false,
        editedProductLoadError: false,
      }),
      success: noopReducer(),
    },
    checkStatement: {
      trigger: (
        state,
        _action: PayloadAction<{
          product: ProductDetailedResponse;
          isAdminMode?: boolean;
          statementId: string;
        }>
      ) => ({
        ...state,
      }),
      success: noopReducer(),
    },
    // checkSecondaryStatement: {
    //   trigger: (state, _action: PayloadAction<{ product: Product }>) => {},
    //   success: noopReducer(),
    //   failure: (state, action: PayloadAction<ApiErrorResponseWithId>) => {},
    // },
    saveProduct: {
      trigger: (
        state,
        _action: PayloadAction<{
          product: ProductDetailedResponse;
          isAdminMode?: boolean;
        }>
      ) => {
        state.productSaving = true;
      },
      success: (state, action: PayloadAction<ProductDetailedResponse>) => {
        productAdapter.upsertOne(
          state.products,
          action as unknown as ProductPageResponse
        );
        state.productSaving = false;
        state.productSaveError = undefined;
      },
      failure: (state, action: PayloadAction<ApiErrorResponseWithId>) => {
        state.productSaving = false;
        state.productSaveError = action;
      },
    },
    deleteProduct: {
      trigger: (state, _action: PayloadAction<{ packagingUnitId: string }>) => {
        state.productDeleting = true;
      },
      success: (state, action: PayloadAction<{ packagingUnitId: string }>) => {
        productAdapter.removeOne(state.products, action.packagingUnitId);
        state.productDeleting = false;
        state.productDeleteError = false;
      },
      failure: (state) => {
        state.productDeleting = false;
        state.productDeleteError = true;
      },
    },
    reset: (_) => initialState,
  },
});

export const ProductSelectors = {
  ...selectors,
  selectProductsFilterStatus: createSelector(
    selectors.selectProductsFilter,
    (filter) => filter.status
  ),
  selectProductList: createSelector(selectors.selectProducts, (products) =>
    Object.values(products.entities)
  ),
  selectEditedProductPackageUnitId: createSelector(
    selectors.selectEditedProduct,
    (product) => product?.packagingUnit?.id
  ),
  selectIsSecondaryProduct: createSelector(
    selectors.selectEditedProduct,
    (product) => product?.secondaryProductSheet
  ),
};
